import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, AlertTriangle, Download, Star, ExternalLink, Camera, FileText, Plane, Globe, PawPrint } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import LoadingExperience from '../components/LoadingExperience';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Business Review URL - PetFlyCheck
const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=12890461172148624086";

// Google Ads conversions (send_to "AW-ID/label" from env); transaction_id = Stripe session for dedup
const ADS_CONVERSIONS = [process.env.REACT_APP_GADS_CONVERSION_1, process.env.REACT_APP_GADS_CONVERSION_2].filter(Boolean);
const trackGoogleAdsConversion = (value, currency, transactionId) => {
  if (typeof window.gtag !== 'function') return;
  ADS_CONVERSIONS.forEach((sendTo) => window.gtag('event', 'conversion', { send_to: sendTo, value, currency, transaction_id: transactionId }));
};

function Result() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = React.useState(true);
  const [verdict, setVerdict] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [conversionTracked, setConversionTracked] = React.useState(false);
  const [retryLoading, setRetryLoading] = React.useState(false);
  
  // Review state
  const [showReviewForm, setShowReviewForm] = React.useState(false);
  const [canReview, setCanReview] = React.useState(false);
  const [reviewRating, setReviewRating] = React.useState(5);
  const [reviewComment, setReviewComment] = React.useState('');
  const [reviewName, setReviewName] = React.useState('');
  const [reviewSubmitting, setReviewSubmitting] = React.useState(false);
  const [reviewSubmitted, setReviewSubmitted] = React.useState(false);

  // Check if analysis failed and retry is available
  const analysisFailedCheck = verdict && (
    verdict.analysis_failed || 
    (verdict.explanation && verdict.explanation.includes('Analysis failed')) ||
    (verdict.crate_checks && Object.values(verdict.crate_checks).every(c => c.notes?.includes('Unable to analyze')))
  );

  // Handle free retry
  const handleRetry = async () => {
    setRetryLoading(true);
    try {
      const response = await axios.post(`${API}/retry-analysis/${sessionId}`);
      toast.success('Free retry granted! You can now upload new photos.');
      // Store retry info and redirect to home with retry mode
      localStorage.setItem('retryToken', response.data.retry_token);
      localStorage.setItem('retrySubmissionId', response.data.submission_id);
      localStorage.setItem('retrySessionId', sessionId);
      navigate('/?retry=true');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to request retry. Please contact support.');
    } finally {
      setRetryLoading(false);
    }
  };

  React.useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const fetchVerdict = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/result/${sessionId}`);
        setVerdict(response.data);
        
        // Track Google Ads conversion when verdict is successfully loaded
        if (!conversionTracked && response.data) {
          const plan = String(response.data.plan_selected || response.data.plan || '');
          const conversionValue = plan.includes('89') || plan === 'full_audit' ? 89 : 39;
          const convKey = `pfc_conv_${sessionId}`;
          if (!localStorage.getItem(convKey)) {
            localStorage.setItem(convKey, '1');
            trackGoogleAdsConversion(conversionValue, 'USD', sessionId);
          }
          setConversionTracked(true);
          
          // Check if user can leave a review
          if (response.data.email) {
            try {
              const reviewCheck = await axios.get(`${API}/reviews/can-review/${response.data.email}`);
              setCanReview(reviewCheck.data.can_review);
            } catch (e) {
              console.log('Could not check review status');
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch verdict:', err);
        setError(err.response?.data?.detail || 'Failed to fetch verdict. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    fetchVerdict();
  }, [sessionId, navigate, conversionTracked]);

  const getStatusIcon = (status) => {
    if (status === 'OK') return <CheckCircle2 className="w-12 h-12 text-green-600" />;
    if (status === 'NOT_OK') return <XCircle className="w-12 h-12 text-red-600" />;
    return <AlertTriangle className="w-12 h-12 text-yellow-600" />;
  };

  const getStatusColor = (status) => {
    if (status === 'OK') return 'bg-green-50 border-green-200 text-green-900';
    if (status === 'NOT_OK') return 'bg-red-50 border-red-200 text-red-900';
    return 'bg-yellow-50 border-yellow-200 text-yellow-900';
  };

  const getStatusText = (status) => {
    if (status === 'OK') return 'COMPLIANT';
    if (status === 'NOT_OK') return 'NOT COMPLIANT';
    return 'NEEDS ATTENTION';
  };

  // Submit review
  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      toast.error('Please write a comment');
      return;
    }
    
    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        email: verdict.email,
        rating: reviewRating,
        comment: reviewComment,
        name: reviewName || undefined
      });
      
      setReviewSubmitted(true);
      setShowReviewForm(false);
      toast.success('Thank you for your review!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Star rating component
  const StarRating = ({ rating, onRatingChange, readonly = false }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && onRatingChange && onRatingChange(star)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          disabled={readonly}
        >
          <Star
            className={`w-6 h-6 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );

  // Show LoadingExperience while loading
  if (loading) {
    return (
      <>
        <div data-testid="confirmation-processing" className="fixed top-0 inset-x-0 z-[200] bg-white/95 border-b border-slate-200 p-3 text-center">
          <h1 className="text-base md:text-lg font-bold text-slate-900">Payment received. Your report is on its way.</h1>
          <p className="text-xs text-slate-600">Checking your crate… this usually takes a few minutes.</p>
        </div>
        <LoadingExperience isVisible={true} estimatedDuration={45} />
      </>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-slate-900 text-white rounded-full px-8 py-3">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!verdict) {
    return null;
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/logo.png" 
                alt="PetFlyCheck Logo" 
                className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
              />
              <span className="text-lg sm:text-xl md:text-3xl font-bold text-slate-900">PetFlyCheck</span>
            </a>
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-full text-xs sm:text-sm md:text-base px-3 sm:px-4 md:px-6 py-2">
              New Check
            </Button>
          </div>
        </div>
      </header>

      <section data-testid="confirmation-block" className="max-w-3xl mx-auto px-4 pt-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Payment received. Your report is on its way.</h1>
        <p data-testid="confirmation-ready" className="text-green-700 font-medium mt-2">Your report is ready.</p>
        {verdict.email_sent_to && (
          <p data-testid="confirmation-emailed" className="text-sm text-slate-700 mt-2">A copy has been emailed to <strong>{verdict.email_sent_to}</strong>.</p>
        )}
        <p className="text-sm text-slate-600 mt-2"><strong>Order reference:</strong> {sessionId?.slice(-12)}</p>
        <p className="text-sm text-slate-600 mt-2"><strong>Need help?</strong> Write to contact@petflycheck.io with your order reference.</p>
        <p className="text-xs text-slate-500 mt-2">PetFlyCheck is independent and not affiliated with IATA or any airline. The airline makes the final decision on acceptance.</p>
      </section>

      {/* Premium Service Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white py-3">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-3">
          <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
          <span className="font-medium">Service Ultra Premium by PetFlyCheck</span>
          <span className="text-blue-200">|</span>
          <span className="text-sm text-blue-100">AI-Assisted IATA Compliance Report</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Status Banner */}
          <div data-testid="verdict-status" className={`rounded-2xl border p-8 ${getStatusColor(verdict.status)}`}>
            <div className="flex items-center justify-center gap-4 mb-4">
              {getStatusIcon(verdict.status)}
              <h2 className="text-3xl font-bold">{getStatusText(verdict.status)}</h2>
            </div>
            <p className="text-center text-sm">
              Confidence: <span className="font-medium uppercase">{verdict.confidence}</span>
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-sm text-yellow-900">
              <strong>Important:</strong> This is an AI-assisted pre-check only. Not a guarantee. Final decision remains with your airline. Always confirm requirements directly with your carrier.
            </p>
          </div>

          {/* Free Retry Banner - Show when analysis failed */}
          {analysisFailedCheck && (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <AlertTriangle className="w-12 h-12 text-red-500" />
                <div>
                  <h3 className="text-lg font-bold text-red-800 mb-2">
                    Analysis Issue Detected
                  </h3>
                  <p className="text-red-700 mb-4">
                    Our AI couldn't properly analyze your photos. This can happen with blurry images, poor lighting, or technical issues.
                    <br /><strong>You're eligible for a FREE retry with new photos!</strong>
                  </p>
                </div>
                <Button
                  data-testid="retry-analysis-btn"
                  onClick={handleRetry}
                  disabled={retryLoading}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg"
                >
                  {retryLoading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Processing...
                    </>
                  ) : (
                    <>
                      🔄 Retry Analysis for FREE
                    </>
                  )}
                </Button>
                <p className="text-xs text-red-600">
                  Tips: Use well-lit photos, show the crate from multiple angles, ensure your pet is visible inside
                </p>
              </div>
            </div>
          )}

          {/* Explanation */}
          {verdict.explanation && (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Summary</h3>
                <p className="text-slate-700 leading-relaxed">{verdict.explanation}</p>
              </CardContent>
            </Card>
          )}

          {/* Flight & Pet Info Banner */}
          {verdict.flight_info && (
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                      <PawPrint className="w-7 h-7 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-amber-700 font-medium">Pet Information</p>
                      <p className="text-lg font-bold text-slate-900 capitalize">{verdict.pet_type || 'Pet'}</p>
                      {verdict.pet_breed && <p className="text-sm text-slate-600">{verdict.pet_breed}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Plane className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Flight Details</p>
                      <p className="text-lg font-bold text-slate-900">{verdict.flight_info.airline || 'Airline'}</p>
                      <p className="text-sm text-slate-600">
                        {verdict.flight_info.origin_country} → {verdict.flight_info.destination_country}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Uploaded Photos Section */}
          {verdict.uploaded_photos && (verdict.uploaded_photos.photo_a || verdict.uploaded_photos.photo_b || verdict.uploaded_photos.photo_c) && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Camera className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Your Uploaded Photos</h3>
                    <p className="text-sm text-slate-500">Analyzed by AI for IATA compliance</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {verdict.uploaded_photos.photo_a && (
                    <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                      <img 
                        src={verdict.uploaded_photos.photo_a}
                        alt="Pet in crate" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-medium">Pet in Crate</p>
                      </div>
                    </div>
                  )}
                  {verdict.uploaded_photos.photo_b && (
                    <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                      <img 
                        src={verdict.uploaded_photos.photo_b}
                        alt="Crate side view" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-medium">Side View</p>
                      </div>
                    </div>
                  )}
                  {verdict.uploaded_photos.photo_c && (
                    <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                      <img 
                        src={verdict.uploaded_photos.photo_c}
                        alt="Crate interior" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-medium">Interior View</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Uploaded Documents Section (Full Audit only) */}
          {verdict.uploaded_documents && Object.values(verdict.uploaded_documents).some(d => d) && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Your Travel Documents</h3>
                    <p className="text-sm text-slate-500">Verified for destination requirements</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {verdict.uploaded_documents.passport && (
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="w-full aspect-[3/4] bg-white rounded border border-slate-200 flex items-center justify-center mb-2 overflow-hidden">
                        <img src={verdict.uploaded_documents.passport} alt="Pet Passport" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs font-medium text-slate-700">Pet Passport</p>
                    </div>
                  )}
                  {verdict.uploaded_documents.rabies && (
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="w-full aspect-[3/4] bg-white rounded border border-slate-200 flex items-center justify-center mb-2 overflow-hidden">
                        <img src={verdict.uploaded_documents.rabies} alt="Rabies Certificate" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs font-medium text-slate-700">Rabies Vaccine</p>
                    </div>
                  )}
                  {verdict.uploaded_documents.health && (
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="w-full aspect-[3/4] bg-white rounded border border-slate-200 flex items-center justify-center mb-2 overflow-hidden">
                        <img src={verdict.uploaded_documents.health} alt="Health Certificate" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs font-medium text-slate-700">Health Certificate</p>
                    </div>
                  )}
                  {verdict.uploaded_documents.microchip && (
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="w-full aspect-[3/4] bg-white rounded border border-slate-200 flex items-center justify-center mb-2 overflow-hidden">
                        <img src={verdict.uploaded_documents.microchip} alt="Microchip Doc" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs font-medium text-slate-700">Microchip</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Crate Checks */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Crate Compliance Checklist</h3>
              <div className="space-y-3">
                {verdict.crate_checks && Object.entries(verdict.crate_checks).map(([key, check]) => (
                  <div key={key} data-testid={`crate-check-${key}`} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                    {check.pass ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 capitalize">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{check.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Document Checks */}
          {verdict.doc_checks && (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Document Compliance Checklist</h3>
                <div className="space-y-3">
                  {Object.entries(verdict.doc_checks).map(([key, check]) => (
                    <div key={key} data-testid={`doc-check-${key}`} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                      {check.pass ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 capitalize">
                          {key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">{check.notes}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {verdict.recommendations && verdict.recommendations.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">What to Fix</h3>
                <ul className="space-y-2">
                  {verdict.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <p className="text-slate-700 flex-1">{rec}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* IATA Airline Rules */}
          {verdict.airline_rules && verdict.airline_rules.rules && verdict.airline_rules.rules.length > 0 && (
            <Card data-testid="airline-rules-section">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {verdict.airline_rules.is_generic 
                        ? 'Règles IATA Génériques'
                        : `Règles IATA - ${verdict.airline_rules.airline_name}`}
                    </h3>
                    {verdict.airline_rules.is_generic && (
                      <p className="text-sm text-slate-500">Règles générales applicables à toutes les compagnies</p>
                    )}
                  </div>
                </div>

                {/* Group rules by category */}
                {(() => {
                  const rulesByCategory = {};
                  verdict.airline_rules.rules.forEach(rule => {
                    const category = rule.category || 'Général';
                    if (!rulesByCategory[category]) {
                      rulesByCategory[category] = [];
                    }
                    rulesByCategory[category].push(rule);
                  });

                  return Object.entries(rulesByCategory).map(([category, rules]) => (
                    <div key={category} className="mb-4 last:mb-0">
                      <h4 className="text-sm font-semibold text-blue-600 mb-2 flex items-center gap-2">
                        {category === 'Cage' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        )}
                        {category === 'Documents' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        {category === 'Restrictions' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        )}
                        {category}
                      </h4>
                      <ul className="space-y-2">
                        {rules.map((rule, idx) => (
                          <li 
                            key={idx} 
                            data-testid={`rule-${category.toLowerCase()}-${idx}`}
                            className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3"
                          >
                            <span className="text-blue-500 mt-0.5">•</span>
                            <span>{rule.rule}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ));
                })()}

                {/* Source link if available */}
                {verdict.airline_rules.rules.some(r => r.source_url) && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500">
                      Sources: Consultez les sites officiels des compagnies pour les règles les plus récentes.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Country Import Rules Section */}
          {verdict.country_rules && (
            <Card data-testid="country-rules-section" className={verdict.country_rules.quarantine ? 'border-red-300 bg-red-50/50' : ''}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${verdict.country_rules.quarantine ? 'bg-red-100' : 'bg-green-100'}`}>
                    <svg className={`w-5 h-5 ${verdict.country_rules.quarantine ? 'text-red-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {verdict.country_rules.name} - Import Requirements
                    </h3>
                    {verdict.country_rules.quarantine && (
                      <p className="text-sm text-red-600 font-medium">⚠️ Quarantine Required: {verdict.country_rules.quarantine_days}</p>
                    )}
                  </div>
                </div>

                {/* Quick facts */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${verdict.country_rules.quarantine ? 'bg-red-100' : 'bg-green-100'}`}>
                    <p className="text-xs text-slate-600">Quarantine</p>
                    <p className={`font-semibold ${verdict.country_rules.quarantine ? 'text-red-700' : 'text-green-700'}`}>
                      {verdict.country_rules.quarantine ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${verdict.country_rules.import_permit ? 'bg-amber-100' : 'bg-green-100'}`}>
                    <p className="text-xs text-slate-600">Import Permit</p>
                    <p className={`font-semibold ${verdict.country_rules.import_permit ? 'text-amber-700' : 'text-green-700'}`}>
                      {verdict.country_rules.import_permit ? 'Required' : 'Not needed'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${verdict.country_rules.blood_test ? 'bg-amber-100' : 'bg-green-100'}`}>
                    <p className="text-xs text-slate-600">Blood Test</p>
                    <p className={`font-semibold ${verdict.country_rules.blood_test ? 'text-amber-700' : 'text-green-700'}`}>
                      {verdict.country_rules.blood_test ? 'Required' : 'Not needed'}
                    </p>
                  </div>
                  {verdict.country_rules.preparation_time && (
                    <div className="p-3 rounded-lg bg-blue-100">
                      <p className="text-xs text-slate-600">Prep Time</p>
                      <p className="font-semibold text-blue-700">{verdict.country_rules.preparation_time}</p>
                    </div>
                  )}
                </div>

                {/* Rules list */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700">Requirements:</h4>
                  {verdict.country_rules.rules.map((rule, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>{rule}</span>
                    </div>
                  ))}
                </div>

                {/* Banned breeds */}
                {verdict.country_rules.banned_breeds && verdict.country_rules.banned_breeds.length > 0 && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">⚠️ Banned/Restricted Breeds in {verdict.country_rules.name}:</h4>
                    <div className="flex flex-wrap gap-2">
                      {verdict.country_rules.banned_breeds.map((breed, idx) => (
                        <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">{breed}</span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Breed-Specific Rules Section */}
          {verdict.breed_rules && (
            <Card data-testid="breed-rules-section" className={
              verdict.breed_rules.risk_level?.includes('HIGH') ? 'border-red-300 bg-red-50/50' :
              verdict.breed_rules.risk_level?.includes('MODERATE') ? 'border-amber-300 bg-amber-50/50' : ''
            }>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      verdict.breed_rules.risk_level?.includes('HIGH') ? 'bg-red-100' :
                      verdict.breed_rules.risk_level?.includes('MODERATE') ? 'bg-amber-100' : 'bg-green-100'
                    }`}>
                      <span className="text-xl">{verdict.pet_type === 'dog' ? '🐕' : '🐈'}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {verdict.breed_rules.name} - Travel Guide
                      </h3>
                      <p className="text-sm text-slate-500 capitalize">{verdict.breed_rules.category?.replace(/-/g, ' ')}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    verdict.breed_rules.risk_level?.includes('HIGH') ? 'bg-red-100 text-red-700' :
                    verdict.breed_rules.risk_level?.includes('MODERATE') ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {verdict.breed_rules.risk_level} Risk
                  </span>
                </div>

                {/* Brachycephalic warning */}
                {verdict.breed_rules.category === 'brachycephalic' && (
                  <div className="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg">
                    <p className="text-red-800 text-sm font-medium">
                      ⚠️ <strong>CRITICAL WARNING:</strong> {verdict.breed_rules.name}s are snub-nosed (brachycephalic) with compromised airways. 
                      This breed faces severe cargo restrictions due to documented in-flight deaths.
                    </p>
                  </div>
                )}

                {/* Airline restrictions */}
                {verdict.breed_rules.airline_restrictions && verdict.breed_rules.airline_restrictions.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-red-700 mb-2">Airline Restrictions:</h4>
                    <div className="space-y-2">
                      {verdict.breed_rules.airline_restrictions.map((restriction, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg p-2">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{restriction}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Health warnings */}
                {verdict.breed_rules.health_warnings && verdict.breed_rules.health_warnings.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-amber-700 mb-2">Health Warnings:</h4>
                    <div className="space-y-2">
                      {verdict.breed_rules.health_warnings.map((warning, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-2">
                          <span className="mt-0.5">⚠️</span>
                          <span>{warning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {verdict.breed_rules.recommendations && verdict.breed_rules.recommendations.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-700 mb-2">Recommendations for Your {verdict.breed_rules.name}:</h4>
                    <div className="space-y-2">
                      {verdict.breed_rules.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-green-700 bg-green-50 rounded-lg p-2">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              data-testid="download-report-btn"
              variant="outline"
              className="flex-1 rounded-full py-6"
              onClick={async () => {
                try {
                  toast.info('Generating PDF...');
                  
                  // Use fetch for better blob handling
                  const response = await fetch(`${API}/verdict-pdf/${sessionId}`);
                  
                  if (!response.ok) {
                    throw new Error('Failed to download PDF');
                  }
                  
                  const blob = await response.blob();
                  
                  // Create download link with proper MIME type
                  const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(pdfBlob);
                  
                  // Create hidden link and trigger download
                  const link = document.createElement('a');
                  link.style.display = 'none';
                  link.href = url;
                  link.download = 'petflycheck_verdict.pdf';
                  
                  // Append to body, click, and cleanup
                  document.body.appendChild(link);
                  link.click();
                  
                  // Cleanup after small delay
                  setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }, 100);
                  
                  toast.success('PDF downloaded successfully!');
                } catch (err) {
                  console.error('PDF download error:', err);
                  // Fallback: open in new tab
                  window.open(`${API}/verdict-pdf/${sessionId}`, '_blank');
                  toast.info('PDF opening in a new tab');
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              data-testid="new-check-btn"
              onClick={() => navigate('/')}
              className="flex-1 bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full py-6"
            >
              Start New Check
            </Button>
          </div>

          {/* Review Section */}
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
            <h3 className="text-lg font-semibold text-slate-900 mb-2 text-center">
              Happy with PetFlyCheck? 🐾
            </h3>
            <p className="text-sm text-slate-600 text-center mb-4">
              Your feedback helps other pet owners travel with confidence
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {/* Google Review Button */}
              <Button
                onClick={() => window.open(GOOGLE_REVIEW_URL, '_blank')}
                variant="outline"
                className="flex items-center gap-2 border-slate-300 hover:bg-white"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Leave a Google Review
                <ExternalLink className="w-4 h-4" />
              </Button>

              {/* Verified Review Button */}
              {canReview && !reviewSubmitted && (
                <Button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Star className="w-5 h-5" />
                  Leave a Verified Review
                </Button>
              )}
              
              {reviewSubmitted && (
                <div className="flex items-center gap-2 text-green-600 font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  Thank you for your review!
                </div>
              )}
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-6 p-4 bg-white rounded-xl border border-slate-200"
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Rating
                    </label>
                    <StarRating rating={reviewRating} onRatingChange={setReviewRating} />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name (optional)
                    </label>
                    <input
                      type="text"
                      value={reviewName}
                      onChange={(e) => setReviewName(e.target.value)}
                      placeholder="John D."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Review *
                    </label>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="How was your experience with PetFlyCheck?"
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowReviewForm(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitReview}
                      disabled={reviewSubmitting}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center">
                    ✓ Verified Purchase • Your review will display a "Verified Buyer" badge
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-slate-400">
            <p>Need help? <a href="/support" className="text-white hover:underline">Contact Support</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Result;
